/**
 * UK Tax Code Validation
 *
 * Validates tax codes against HMRC formats.
 */

// Tax code regex patterns matching HMRC formats
// Standard: 1-4 digits + L/M/N/T suffix (e.g., 1257L, 0L)
const STANDARD_TAX_CODE_PATTERN = /^(\d{1,4})([LMNT])(\s+(W1|M1|X))?$/i;

// Special codes: BR, D0, D1, 0T, NT
const SPECIAL_TAX_CODE_PATTERN = /^(BR|D0|D1|0T|NT)(\s+(W1|M1|X))?$/i;

// K codes: K + 1-4 digits (e.g., K100)
const K_TAX_CODE_PATTERN = /^K(\d{1,4})(\s+(W1|M1|X))?$/i;

// Scottish codes: S prefix + standard pattern (e.g., S1257L)
const SCOTTISH_TAX_CODE_PATTERN = /^S(\d{1,4})([LMNT])(\s+(W1|M1|X))?$/i;

// Welsh codes: C prefix + standard pattern (e.g., C1257L)
const WELSH_TAX_CODE_PATTERN = /^C(\d{1,4})([LMNT])(\s+(W1|M1|X))?$/i;

export interface TaxCodeValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate a UK tax code against HMRC formats.
 *
 * Supported formats:
 * - Standard: 1257L, 1100L, 0L, 1257M, 1257N, 1257T
 * - Special: BR, D0, D1, 0T, NT
 * - K codes: K100, K1234
 * - Scottish: S1257L
 * - Welsh: C1257L
 * - Emergency suffixes: W1, M1, X (e.g., 1257L W1)
 *
 * @param taxCode The tax code to validate
 * @returns Validation result with valid flag and optional error message
 */
export function validateTaxCode(taxCode: string): TaxCodeValidationResult {
  if (!taxCode || !taxCode.trim()) {
    return {
      valid: false,
      error: "Tax code is required",
    };
  }

  const code = taxCode.trim().toUpperCase();

  const patterns = [
    STANDARD_TAX_CODE_PATTERN,
    SPECIAL_TAX_CODE_PATTERN,
    K_TAX_CODE_PATTERN,
    SCOTTISH_TAX_CODE_PATTERN,
    WELSH_TAX_CODE_PATTERN,
  ];

  for (const pattern of patterns) {
    if (pattern.test(code)) {
      return { valid: true };
    }
  }

  return {
    valid: false,
    error: `Invalid tax code "${taxCode}". Examples: 1257L, BR, 0T, K100, S1257L`,
  };
}

/**
 * Check if a tax code is valid (simple boolean check).
 *
 * @param taxCode The tax code to validate
 * @returns true if valid, false otherwise
 */
export function isValidTaxCode(taxCode: string): boolean {
  return validateTaxCode(taxCode).valid;
}
